import { v4 as uuid } from 'uuid';

import { KARAOKE_TRACK_NUMBER } from '../constants';
import { Element } from './Element';
import { AudioElement } from './elements/AudioElement';
import { ImageElement } from './elements/ImageElement';
import { ShapeElement } from './elements/ShapeElement';
import { TextElement } from './elements/TextElement';
import { VideoElement } from './elements/VideoElement';

import type { TrackType } from '../types/TrackType';

/**
 * Track represents a timeline track that contains elements
 */
export class Track {
  // Private properties with read-only public access
  private _id: string;
  private _type: TrackType;
  private _name: string;
  private _elements: Element[];
  private _locked: boolean;
  private _visible: boolean;

  constructor(props: Partial<Track> = {}) {
    this._id = props.id ?? uuid();
    this._type = props.type ?? 'video';
    this._name =
      props.name ??
      `${this._type.charAt(0).toUpperCase() + this._type.slice(1)} Track`;
    this._elements = props.elements ? [...props.elements] : [];
    this._locked = props.locked ?? false;
    this._visible = props.visible ?? true;
  }

  // Getters
  get id(): string {
    return this._id;
  }

  get type(): TrackType {
    return this._type;
  }

  get name(): string {
    return this._name;
  }

  get elements(): readonly Element[] {
    return this._elements.sort((a, b) => a.time - b.time);
  }

  findElementAtTime(time: number): Element | null {
    for (const el of this.elements) {
      if (el.time <= time && time < el.end_time) {
        return el;
      }
    }
    return null;
  }

  printElements(): void {
    // Sort and print elements
    const sortedElements = this.sortedElements;
    console.log(
      `Track ${this._name} (${this._type}) - Elements:`,
      sortedElements.map(el => ({
        id: el.id,
        type: el.type,
        time: el.time,
        end_time: el.end_time,
        track: el.track,
      })),
    );
  }

  /*
   *
   * @returns {Element[]} An array of elements open for editing
   */
  get mutableElements(): Element[] {
    return this._elements.filter(el => !el.locked);
  }

  get locked(): boolean {
    return this._locked;
  }

  get visible(): boolean {
    return this._visible;
  }

  // Setters as methods
  setId(id: string): void {
    this._id = id;
  }

  setName(name: string): void {
    if (!name || name.trim() === '') {
      throw new Error('Track name cannot be empty');
    }
    this._name = name;
  }

  setType(type: TrackType): void {
    this._type = type;
  }

  setLocked(locked: boolean): void {
    this._locked = locked;
  }

  setVisible(visible: boolean): void {
    this._visible = visible;
  }

  /**
   * Adds an element to the track
   */
  addElement(element: Element): void {
    // Ensure the element has the correct track number
    // Track ID should be at least 1 for Renderer compatibility
    const trackNumber = Math.max(1, Number(this._id));
    element.update({ track: trackNumber });

    // Create a new array instead of mutating
    this._elements = [...this._elements, element];
  }

  /**
   * Removes an element from the track by id
   */
  removeElement(elementId: string): boolean {
    const initialLength = this._elements.length;

    // Create a new array instead of mutating
    this._elements = this._elements.filter(el => el.id !== elementId);

    return this._elements.length !== initialLength;
  }

  /**
   * Updates an element in the track
   */
  updateElement(elementId: string, props: Partial<Element>): boolean {
    let elementFound: Element | undefined;
    // find element by id or find nested element in composition
    for (const element of this.elements) {
      if (element.id === elementId) {
        elementFound = element;
      }
      if (Element.isCompositionElement(element)) {
        const foundNestedElement = element.findElement(
          el => el.id === elementId,
        );
        if (foundNestedElement) {
          elementFound = foundNestedElement;
        }
      }
    }

    if (elementFound) {
      elementFound.update(props);
      return true;
    }
    return false;
  }

  /**
   * Replaces an element with a new element while preserving its position in the track
   * This is used for element type transitions (e.g., ImageElement ↔ CompositionElement)
   */
  replaceElement(elementId: string, newElement: Element): boolean {
    const elementIndex = this._elements.findIndex(el => el.id === elementId);
    if (elementIndex !== -1) {
      // Ensure the new element has the same ID to maintain references
      newElement.id = elementId;
      // Replace the element at the same position
      this._elements[elementIndex] = newElement;
      return true;
    }
    return false;
  }

  /**
   * Returns all elements sorted by start time
   */
  get sortedElements(): readonly Element[] {
    // Create a new sorted array without modifying original
    return [...this._elements].sort((a, b) => a.time - b.time);
  }

  /**
   * Checks if an element can be added at the specified time without overlapping
   */
  canAddElementAt(startTime: number, duration: number): boolean {
    return !this._elements.some(element => {
      // Handle special case where duration is a string (like 'media')
      const elementDuration =
        typeof element.duration === 'number' ? element.duration : 0;
      const elementEnd = element.time + elementDuration;
      const newElementEnd = startTime + duration;

      return startTime < elementEnd && newElementEnd > element.time;
    });
  }

  /**
   * Creates a deep clone of this track
   */
  clone(): Track {
    return new Track({
      id: this._id,
      type: this._type,
      name: this._name,
      elements: this._elements.map(element => element.clone()),
      locked: this._locked,
      visible: this._visible,
    });
  }

  /**
   * Converts the track to JSON compatible with the Renderer
   */
  toJSON(): Record<string, any>[] {
    return this._elements.map(element => element.toJSON());
  }

  /**
   * Creates a Track from an array of elements
   */
  static fromElements(
    elements: Record<string, any>[],
    trackType: 'video' | 'audio' | 'text' | 'image' | 'shape' = 'video',
  ): Track {
    console.log(
      'Creating Track from elements:',
      JSON.stringify(elements, null, 2),
    );

    // Filter out any null or undefined elements
    const validElements = elements.filter(el => el != null);

    if (validElements.length === 0) {
      console.warn('No valid elements provided to Track.fromElements');
      return new Track({ type: trackType });
    }

    const trackElements = validElements.map(json => {
      // Create a copy to avoid modifying the original
      const elementJson = { ...json };

      // Check for source object type first - it's often more accurate
      if (
        elementJson.source &&
        typeof elementJson.source === 'object' &&
        elementJson.source.type
      ) {
        const sourceType = elementJson.source.type;
        if (['video', 'text', 'audio', 'image', 'shape'].includes(sourceType)) {
          elementJson.type = sourceType;
          console.log(`Using source.type ${sourceType} for element`);
        }
      }

      // Ensure type exists and is valid before proceeding
      if (!elementJson.type) {
        console.warn(
          `Element missing type property, assuming type: ${trackType}`,
        );
        elementJson.type = trackType; // Default to track type
      }

      // Ensure ID exists
      if (!elementJson.id) {
        elementJson.id = `generated-${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 9)}`;
        console.warn(`Generated missing ID for element: ${elementJson.id}`);
      }

      // Map globalTime/localTime to startTime if needed
      if (
        elementJson.start === undefined &&
        elementJson.globalTime !== undefined
      ) {
        elementJson.start = elementJson.globalTime;
      }

      console.log('Creating element from JSON:', {
        type: elementJson.type,
        id: elementJson.id,
        start: elementJson.start || elementJson.globalTime || 0,
        duration: elementJson.duration,
      });

      try {
        // Create the right element type based on valid type property
        let element: Element;

        switch (elementJson.type) {
          case 'video':
            element = VideoElement.fromJSON(elementJson);
            break;
          case 'text':
            element = TextElement.fromJSON(elementJson);
            break;
          case 'audio':
            element = AudioElement.fromJSON(elementJson);
            break;
          case 'image':
            element = ImageElement.fromJSON(elementJson);
            break;
          case 'shape':
            element = ShapeElement.fromJSON(elementJson);
            break;
          default:
            console.warn(
              `Unknown element type: "${elementJson.type}", creating generic Element`,
            );
            // Set a valid type on the JSON before creating the element
            elementJson.type = 'video'; // Default to video for unknown types
            element = Element.fromJSON(elementJson);
        }

        // Verify the created element has required properties
        if (element && element.id && element.type) {
          return element;
        } else {
          console.error('Created invalid element:', element);
          // Create a fallback element
          return new Element({
            id: elementJson.id || `fallback-${Date.now()}`,
            type: 'video',
            time: elementJson.start || elementJson.globalTime || 0,
            duration: elementJson.duration || 0,
            track: elementJson.track || 1,
          });
        }
      } catch (error) {
        console.error('Error creating element from JSON:', error);
        // Return a fallback element instead of crashing
        return new Element({
          id: elementJson.id || `error-${Date.now()}`,
          type: 'video',
          time: elementJson.start || elementJson.globalTime || 0,
          duration: elementJson.duration || 0,
          track: elementJson.track || 1,
        });
      }
    });

    // Get track ID from first element, ensure it's at least 1
    const trackId = Math.max(1, trackElements[0]?.track || 1);

    return new Track({
      type: trackType,
      elements: trackElements,
      id: String(trackId),
    });
  }

  public static isSupporting(trackId: string | number): boolean {
    const numericId = Number(trackId);
    return !Number.isNaN(numericId) && numericId > KARAOKE_TRACK_NUMBER;
  }
}
